import React, { useCallback, useEffect, useMemo, useState } from "react"
import { DropzoneState } from "react-dropzone"
import { useDispatch } from "react-redux"
import {
    Text,
    ProgressBar,
    DropzoneArea,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Pagination,
    PaginationItem
} from "dash-styled"

import { Uploader } from "../../../../Common/Uploader/Uploader"
import {
    UploadStatus,
    UploadQueueItem,
    isFinishedOrFailed,
    getProgress
} from "../../../../../../../Models/Client/UploadQueueItem"
import {
    FileType,
    toAcceptedMimeType
} from "../../../../../../../Models/Client/FileType"
import {
    removeQueueItem,
    uploadQueuedItem
} from "../../../../../../../Actions/Client/Interface/UploaderActions"
import { useAppState } from "../../../../../../../Hooks/useReduxState"
import { UploadableRow } from "./UploadableRow"

const ROWS_PER_PAGE = 10

interface Props {
    testId?: string
    stateKey: string
    type?: FileType
    single: boolean
    onChange: (value: (string | undefined)[]) => void
}

const renderDropZoneContent =
    (multiple: boolean) =>
    ({
        getRootProps,
        getInputProps,
        isDragAccept
    }: DropzoneState): JSX.Element =>
        (
            <div {...getRootProps()}>
                <DropzoneArea
                    inputProps={getInputProps()}
                    acceptDrop={isDragAccept}
                    dragText={`Drag your file${multiple ? "s" : ""} here`}
                    dropText={`Drop your file${multiple ? "s" : ""} here`}
                    buttonText="Browse"
                    className="bg-rhino-100 py-12 rounded border-2 border-dashed border-rhino-500"
                />
            </div>
        )

export const Uploadable: React.FC<Props> = ({
    type,
    stateKey,
    onChange,
    testId,
    single
}: Props) => {
    const queue = useAppState(
        state => state.client.interface.uploader[stateKey] || []
    )
    const dispatch = useDispatch()
    const [page, setPage] = useState(0)
    const emptyRows =
        ROWS_PER_PAGE -
        Math.min(ROWS_PER_PAGE, queue.length - page * ROWS_PER_PAGE)

    const progressSum =
        queue.reduce((sum, item) => sum + getProgress(item), 0) / queue.length
    const toProcessCount =
        queue.length - queue.filter(isFinishedOrFailed).length

    const multiple = useMemo(
        () =>
            single
                ? {
                      allowMultiple: false,
                      multipleNotAllowedMessage:
                          "Right now we just need one file. Once you’re set up you’ll be able to upload many at a time"
                  }
                : true,
        [single]
    )

    useEffect(() => {
        onChange(
            queue.map(item =>
                item.status === UploadStatus.Finished ? item.url : undefined
            )
        )
    }, [onChange, queue])

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value - 1)
    }

    const renderItem = useCallback(
        (item: UploadQueueItem, index: number) => {
            const handleRemove = () => dispatch(removeQueueItem(stateKey, item))
            const handleRetry = () => {
                if (item.status === UploadStatus.Failed) {
                    dispatch(uploadQueuedItem(stateKey, item, true))
                }
            }

            return (
                <UploadableRow
                    key={index}
                    item={item}
                    type={type}
                    onRemove={handleRemove}
                    onRetry={handleRetry}
                />
            )
        },
        [dispatch, stateKey, type]
    )

    return (
        <div className="p-6" data-testid={testId}>
            {(!single || queue.length === 0) && (
                <Uploader
                    stateKey={stateKey}
                    accept={type === undefined ? "" : toAcceptedMimeType(type)}
                    multiple={multiple}
                    renderDropZoneContent={renderDropZoneContent(!single)}
                    squashSuccessMessage={true}
                    appendTimestamp={true}
                />
            )}
            {queue.length > 0 && (
                <div>
                    {toProcessCount > 0 && (
                        <div className="mt-6">
                            <ProgressBar
                                fill={progressSum}
                                thickness="lg"
                                color="yellow"
                            />
                            <Text className="mt-0 mb-2 text-rhino-900">
                                Uploading{" "}
                                {!single ? `(${toProcessCount} remaining)` : ""}
                            </Text>
                        </div>
                    )}
                    {queue.length <= ROWS_PER_PAGE ? (
                        <div className="pt-3">{queue.map(renderItem)}</div>
                    ) : (
                        <TableContainer className="overflow-hidden">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="border-0">
                                            <Pagination
                                                shape="rounded"
                                                classes={{
                                                    ul: "justify-end"
                                                }}
                                                count={Math.ceil(
                                                    queue.length / ROWS_PER_PAGE
                                                )}
                                                page={page + 1}
                                                onChange={handleChange}
                                                renderItem={props => (
                                                    <PaginationItem
                                                        {...props}
                                                        className="font-averta"
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {queue
                                        .slice(
                                            page * ROWS_PER_PAGE,
                                            page * ROWS_PER_PAGE + ROWS_PER_PAGE
                                        )
                                        .map((item, index) => (
                                            <TableRow key={item.objectUrl}>
                                                <TableCell className="border-0 p-0 font-averta">
                                                    {renderItem(item, index)}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                    {emptyRows > 0 && (
                                        <TableRow
                                            style={{
                                                height: 53 * emptyRows
                                            }}
                                        >
                                            <TableCell colSpan={6} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </div>
            )}
        </div>
    )
}
